// /app/notes — the chapter list (BRIEFS C4.3). Released chapters only, as cards grouped by
// class (XI first). A card opens /app/notes/<chapter_id>; the chapter page itself goes back
// to the unit read last on this laptop.
import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useActivityArea } from '../../components/app/activity';
import { getChapters } from '../../api/v2';
import { IconChevronRight, IconNotes } from '../../components/app/icons';
import { C, FONT } from '../../components/app/theme';
import { LoadError, Loading } from '../../components/app/LoadState';

const num = (n) => Number(n || 0).toLocaleString('en-IN');

function ChapterCard({ ch }) {
  return (
    <Link to={`/app/notes/${ch.chapter_id}`} data-testid={`v2-notes-chapter-${ch.chapter_id}`} style={{
      flex: '1 1 280px', maxWidth: 420, boxSizing: 'border-box', minHeight: 96,
      padding: '16px 18px', background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 12, display: 'flex', alignItems: 'center', gap: 14,
      textDecoration: 'none', color: C.text,
    }}>
      <div aria-hidden="true" style={{
        width: 44, height: 44, borderRadius: 10, background: C.tint, color: C.primary,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}><IconNotes /></div>
      <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Chapter {ch.ncert_no}</div>
        <div style={{ fontFamily: FONT.heading, fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>{ch.title}</div>
        <div style={{ fontSize: 13, color: C.muted }}>{num(ch.points)} points</div>
      </div>
      <IconChevronRight style={{ color: C.muted, flexShrink: 0 }} />
    </Link>
  );
}

export default function AppNotesList() {
  useActivityArea('notes');
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['v2-chapters'], queryFn: getChapters });

  let body;
  if (isLoading) {
    body = <Loading />;
  } else if (isError) {
    body = <LoadError onRetry={refetch} />;
  } else if (!data || data.length === 0) {
    body = (
      <div style={{ padding: 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, fontSize: 15, color: C.muted }}>
        Your chapters will show here when they are ready.
      </div>
    );
  } else {
    body = ['XI', 'XII'].map((cls) => {
      const list = data.filter((c) => c.class_level === cls);
      if (!list.length) return null;
      return (
        <section key={cls} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 18, fontWeight: 600 }}>Class {cls}</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
            {list.map((ch) => <ChapterCard key={ch.chapter_id} ch={ch} />)}
          </div>
        </section>
      );
    });
  }

  return (
    <div style={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 28, fontWeight: 600 }}>Notes</h1>
        <div style={{ fontSize: 15, color: C.muted }}>Choose a chapter.</div>
      </div>
      {body}
    </div>
  );
}
